import {FC, useEffect, useState} from 'react';

import {Chip, Link, Box as MBox, styled, Typography, Radio, Box} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Else, If, Then, When} from 'react-if';

import AddIcon from 'assets/icons/addIcon';
import LargeAddIcon from 'assets/icons/largeAddIcon';
import {DefaultThemeOptions} from 'config/theme';
import {DEFAULT_LANGUAGE} from 'constants/index';
import {useAppMode} from 'hooks/useAppMode';
import ReactNative from 'services/react-native';
import {UserAddress} from 'types';
import {getStringFormOfAddress} from 'utils';

import {ExpressModal} from './ExpressModal';

export type AddressDrawerBodyProps = {
  onEditAddress?: (address: UserAddress) => void;
  sortedAddresses: UserAddress[];
  onAddressClick: (address: UserAddress) => void;
  selectedShippingAddress: UserAddress | undefined;
  onNewAddress: () => void;
};

const theme = DefaultThemeOptions;

export const StyledAddressDrawerBodyWrapper = styled(Box)(({theme}) => ({
  width: '100%',
  backgroundColor: theme.palette.common.mds_global_color_white,
  flex: '1 1 0%',
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingBottom: '20px'
}));

export const StyledUlContainer = styled('ul')(({theme}) => ({
  overflowX: 'hidden',
  width: '100%',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5)
}));

export const StyledLI = styled('li')<{isReactNativeView?: boolean}>(({theme, isReactNativeView}) => ({
  width: '-webkit-fill-available',
  padding: isReactNativeView ? theme.spacing(0, 2) : theme.spacing(0, 4)
}));

export const StyledSpan = styled('span')(({theme}) => ({
  fontWeight: 'normal',
  color: theme.palette.grey[400]
}));

export const StyledLink = styled(Link)<{isReactNativeView?: boolean}>(({isReactNativeView}) => ({
  fontWeight: isReactNativeView ? 700 : 400,
  textDecoration: 'none',
  fontSize: isReactNativeView ? '16px' : '14px'
}));

export const StyledChip = styled(Chip)(() => ({
  borderRadius: 4,
  marginTop: 10,
  fontWeight: 'bold',
  fontSize: 14
}));

export const StyledRadio = styled(Radio)(() => ({
  padding: 0,
  height: 'fit-content'
}));

export const PlusIcon = styled(AddIcon)(({theme}) => ({
  cursor: 'pointer',
  fontSize: theme.spacing(3)
}));

export const AddNewAddressButton = styled('div')<{isReactNativeView?: boolean}>(({theme, isReactNativeView}) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: isReactNativeView ? theme.spacing(1) : theme.spacing(1.5),
  marginTop: theme.spacing(1),
  borderColor: theme.palette.grey[100],
  borderWidth: '1px',
  borderRadius: theme.spacing(1),
  borderStyle: 'solid',
  paddingLeft: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  ':hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.grey[50]
  }
}));

export const AddressDetailBoxWrapper = styled(MBox, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'isExpressIneligible'
})<{active?: boolean; isExpressIneligible?: boolean}>(({theme, active, isExpressIneligible}) => ({
  opacity: isExpressIneligible ? 0.5 : 1,
  borderColor: theme.palette.grey[100],
  borderWidth: '1px',
  borderRadius: theme.spacing(1),
  backgroundColor: active ? theme.palette.grey[100] : theme.palette.common.mds_global_color_transparent,
  borderStyle: 'solid',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.grey[50]
  }
}));

export const AddressDetailBox = styled(MBox)(({theme}) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1.5)
}));

export const AddressDetailMain = styled(MBox)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1
}));

export const LoaderWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%'
});

export const AddressDrawerBody: FC<AddressDrawerBodyProps> = ({
  onEditAddress: handleEditAddress,
  sortedAddresses,
  onAddressClick: handleAddressClick,
  selectedShippingAddress,
  onNewAddress: handleAddNewAddress
}) => {
  const {t, i18n} = useTranslation();
  const {isExpress} = useAppMode();

  const [selectedAddressId, setSelectedAddressId] = useState<string | undefined>(selectedShippingAddress?.id);
  const [isExpressModalOpen, setExpressModalOpen] = useState<boolean>(false);
  const isThai = i18n.language === DEFAULT_LANGUAGE;

  useEffect(() => {
    setSelectedAddressId(selectedShippingAddress?.id);
  }, [selectedShippingAddress]);

  const isReactNativeView = ReactNative.isReactNativeWebView();

  const {availableAddresses, unavailableAddresses} = sortedAddresses.reduce(
    (accum, ad) => {
      if (ad.isEligibleForExpress) {
        accum.availableAddresses.push(ad);
        return accum;
      }
      accum.unavailableAddresses.push(ad);
      return accum;
    },
    {
      availableAddresses: [] as UserAddress[],
      unavailableAddresses: [] as UserAddress[]
    }
  );

  const getPhoneNumberWithSpace = (phone: string) => {
    if (!phone) return;
    let formatedPhone = '';
    const array = phone.replace(/\s+/g, '').split('');

    const chunkSize = 3;
    for (let i = 0; i < array.length; i += chunkSize) {
      const chunk = array.slice(i, i + chunkSize);
      const chunkString = i == 0 || chunk.length < chunkSize ? chunk.join('') : ' ' + chunk.join('');
      formatedPhone += chunkString;
    }

    return formatedPhone;
  };

  const toggleExpressModal = () => {
    setExpressModalOpen((prev) => !prev);
  };

  const _renderAddress = (address: UserAddress, isExpressIneligible?: boolean) => (
    <StyledLI key={address.id} isReactNativeView={isReactNativeView}>
      <AddressDetailBoxWrapper
        py={3 / 2}
        px={2}
        isExpressIneligible={isExpressIneligible}
        active={selectedAddressId === address.id}
        onClick={() => {
          if (isExpressIneligible) return;
          setSelectedAddressId(address.id);
          handleAddressClick && handleAddressClick(address);
        }}
      >
        <AddressDetailBox>
          <div>
            <StyledRadio size="small" disableRipple checked={selectedAddressId === address.id} />
          </div>
          <AddressDetailMain>
            <div>
              <Typography fontWeight="600" sx={{wordBreak: 'break-word', overflowWrap: 'break-word'}}>
                {address.firstName} {address.lastName},{' '}
                <StyledSpan>{getPhoneNumberWithSpace(address.mobile)}</StyledSpan>
              </Typography>
            </div>
            <div>
              <Typography mt={0.5} color={theme.palette.grey[400]} fontSize={14}>
                {getStringFormOfAddress(address, isThai, true)}
              </Typography>
            </div>
            <When condition={address.isDefault}>
              <div>
                <StyledChip label={t('message.defaultLabelAddress')} size="small" color="primary" variant="outlined" />
              </div>
            </When>
          </AddressDetailMain>
          <When condition={typeof handleEditAddress === 'function' && !isExpressIneligible}>
            <div id="btn_edit" style={{display: 'flex', alignItems: isReactNativeView ? 'center' : 'flex-start'}}>
              <StyledLink
                variant="body2"
                onClick={() => {
                  handleEditAddress && handleEditAddress(address);
                }}
                isReactNativeView={isReactNativeView}
              >
                {t('button.edit')}
              </StyledLink>
            </div>
          </When>
        </AddressDetailBox>
      </AddressDetailBoxWrapper>
    </StyledLI>
  );

  return (
    <StyledAddressDrawerBodyWrapper>
      <StyledUlContainer>
        <StyledLI isReactNativeView={isReactNativeView}>
          <AddNewAddressButton
            onClick={() => {
              handleAddNewAddress && handleAddNewAddress();
            }}
            isReactNativeView={isReactNativeView}
          >
            {isReactNativeView ? <LargeAddIcon /> : <PlusIcon />}
            <div>
              <Typography fontWeight={isReactNativeView ? '700' : '600'}>{t('button.addNewAddress')}</Typography>
            </div>
          </AddNewAddressButton>
        </StyledLI>
        <If condition={isExpress}>
          <Then>
            <Box display="flex" flexDirection="column">
              {availableAddresses.map((address) => _renderAddress(address))}
              <When condition={unavailableAddresses.length}>
                <Typography color="grey.400" marginY={1.5} paddingX={4} fontSize={14} fontWeight={400}>
                  {t('express.title.unavailableAddresses')}{' '}
                  <Typography
                    fontSize={14}
                    fontWeight={400}
                    component="span"
                    color="#E82026"
                    style={{textDecoration: 'underline'}}
                    onClick={toggleExpressModal}
                  >
                    {t('express.title.learnMore')}
                  </Typography>
                </Typography>
                {unavailableAddresses.map((address) => _renderAddress(address, true))}
              </When>
            </Box>
          </Then>
          <Else>{sortedAddresses.map((address) => _renderAddress(address))}</Else>
        </If>
      </StyledUlContainer>
      <ExpressModal isOpen={isExpressModalOpen} onClose={toggleExpressModal} />
    </StyledAddressDrawerBodyWrapper>
  );
};

AddressDrawerBody.displayName = 'AddressDrawerBody';
export default AddressDrawerBody;
